import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import { FileTypeDatasetAdapter, formatAdapts, compressionAdapts } from './file-type.dataset.adapter';
import { HttpFileDataset } from 'app/model/dataset/http-file.dataset';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Owned } from 'app/model/base/named';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HttpFileDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                format: formatAdapts,
                compression: compressionAdapts,
                relativeUrl: '*',
                requestMethod: '*',
                additionalHeaders: '*',
                requestBody: '*'
            }
        }
    }
})
export class HttpFileDatasetAdapter extends FileTypeDatasetAdapter<HttpFileDataset, Model.DatasetResource> {
    protected async _toResource(dataset: HttpFileDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.HttpDataset;
        modelDataset.type = Metamodel.modelFor(HttpFileDataset).name;
        modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        modelDataset.typeProperties = {
            ...this.formatAndCompressionValueToResource(dataset.format, dataset.compression),
            relativeUrl: ValueAdapter.toResource(dataset, HttpFileDataset.URL),
            requestMethod: ValueAdapter.toResource(dataset, HttpFileDataset.METHOD),
            additionalHeaders: ValueAdapter.toResource(dataset, HttpFileDataset.HEADER),
            requestBody: ValueAdapter.toResource(dataset, HttpFileDataset.BODY)
        };

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: HttpFileDataset): Promise<HttpFileDataset> {
        if (!dataset) {
            dataset = HttpFileDataset.MODEL.create<HttpFileDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.HttpDataset;
        if (model) {
            if (model.typeProperties != null) {
                Owned.own(dataset, 'format', this.formatResourceToValue(model.typeProperties.format));
                dataset.compression = this.compressionResourceToValue(model.typeProperties.compression);
                ValueAdapter.toModel(dataset, HttpFileDataset.URL, model.typeProperties.relativeUrl);
                if (typeof model.typeProperties.requestMethod === 'string') {
                    dataset.requestMethod = stringsAreEqualCaseInsensitive(model.typeProperties.requestMethod, 'GET') ?
                        'GET' : stringsAreEqualCaseInsensitive(model.typeProperties.requestMethod, 'POST') ?
                            'POST' : model.typeProperties.requestMethod;
                } else {
                    ValueAdapter.toModel(dataset, HttpFileDataset.METHOD, model.typeProperties.requestMethod);
                }
                ValueAdapter.toModel(dataset, HttpFileDataset.HEADER, model.typeProperties.additionalHeaders);
                ValueAdapter.toModel(dataset, HttpFileDataset.BODY, model.typeProperties.requestBody);
            } else {
                this.initializeModel(dataset);
            }
            dataset.columns = this.columnResourceToValue(dataset, model.structure);
        }
        // TODO: add more properties here

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, HttpFileDataset.MODEL));
        return dataset;
    }
}
